import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { CompanyNewsItem, StockPrice, UserProfile } from '../../models/profile.model';
import { NeonDataComponent } from '../neon-data/neon-data.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, NeonDataComponent],
  templateUrl: './dashboard.component.html',
  styles: [`
    .quote-card { overflow: hidden; }
    .quote-card-header { display: flex; justify-content: space-between; gap: 16px; align-items: center; margin-bottom: 4px; }
    .quote-table th:last-child, .quote-table td:last-child { width: 90px; text-align: right; }
    .company-name { font-weight: 700; }
    .expand-news { min-width: 68px; padding: 6px 9px; border: 1px solid #9ccfc1; border-radius: 4px; color: #087b63; background: #f1fbf7; font-size: .76rem; font-weight: 700; }
    .expand-news:hover { color: #fff; background: #087b63; opacity: 1; }
    .news-detail td { padding: 0; background: #f8fbfa; }
    .news-panel { padding: 18px 20px 20px 24px; border-left: 4px solid #0fa77f; }
    .news-heading { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; margin-bottom: 12px; }
    .news-heading h3 { margin: 0; color: #1e3b35; font-size: .94rem; }
    .news-list { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
    .news-list a { color: #1e4c77; font-weight: 600; text-decoration: none; }
    .news-list a:hover { color: #087b63; text-decoration: underline; }
    .news-meta { margin-top: 3px; color: #6c7b88; font-size: .76rem; }
    .news-state { margin: 0; color: #6c7b88; font-size: .84rem; }
    @media (max-width: 680px) { .quote-card-header { align-items: flex-start; flex-direction: column; } .quote-table { min-width: 720px; } .news-panel { padding: 16px; } }
  `],
})
export class DashboardComponent implements OnInit, OnDestroy {
  profile: UserProfile | null = null;
  quotes: StockPrice[] = [];
  loading = false;
  loadingNews = false;
  errorMessage = '';
  newsError = '';
  lastRefreshed: Date | null = null;
  newsByCompany: Record<string, CompanyNewsItem[]> = {};
  expandedCompanies = new Set<string>();

  private refreshHandle: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_REFRESH_MS = 60000; // refresh live prices every 60s

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    this.api.getProfile().subscribe({
      next: (p) => {
        this.profile = p;
        this.refresh();
        this.refreshHandle = setInterval(() => this.refresh(), this.AUTO_REFRESH_MS);
      },
      error: () => {
        this.errorMessage = 'No profile found yet. Please fill in your profile first.';
      },
    });
  }

  ngOnDestroy(): void {
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
    }
  }

  refresh(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api.getLivePortfolio().subscribe({
      next: (res) => {
        this.quotes = res.quotes;
        this.loading = false;
        this.lastRefreshed = new Date();
        this.loadNews();
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.detail || 'Failed to fetch live prices.';
      },
    });
  }

  isCompanyExpanded(company: string): boolean {
    return this.expandedCompanies.has(company);
  }

  toggleCompanyNews(company: string): void {
    if (this.expandedCompanies.has(company)) {
      this.expandedCompanies.delete(company);
    } else {
      this.expandedCompanies.add(company);
    }
  }

  getCompanyNews(company: string): CompanyNewsItem[] {
    return this.newsByCompany[company] || [];
  }

  private loadNews(): void {
    this.loadingNews = true;
    this.newsError = '';
    this.api.getPortfolioNews(3).subscribe({
      next: (response) => {
        this.newsByCompany = response.news;
        this.loadingNews = false;
      },
      error: () => {
        this.loadingNews = false;
        this.newsError = 'Latest news is unavailable right now.';
      },
    });
  }
}
